import { FormEvent, useState } from "react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { usePinnedArtifactMutations } from "./usePinnedArtifacts";

interface PinArtifactDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  conversationId: string;
  artifactName: string;
  defaultDisplayName: string;
}

// Names the card and confirms the pin. The workflow behind it is derived
// server-side from the artifact's lineage, so there is nothing to choose here —
// the dialog exists to give the card a label and to say plainly that pinning
// captures how the artifact was made, which is the part that is not obvious.
//
// Mounted only while open, so the name resets to the artifact's own summary
// without an effect to reset it.
export const PinArtifactDialog = ({
  open,
  onClose,
  projectId,
  conversationId,
  artifactName,
  defaultDisplayName,
}: PinArtifactDialogProps) => {
  const [displayName, setDisplayName] = useState(defaultDisplayName);
  const { pin, isMutating } = usePinnedArtifactMutations(projectId);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const created = await pin({
      conversation_id: conversationId,
      artifact_name: artifactName,
      display_name: displayName.trim() || defaultDisplayName,
    });
    if (created) onClose();
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title="Pin to project overview"
      description="Everyone in the project will see this. Pinning also captures how the artifact was made, so it can be rebuilt from its sources later."
      primaryLabel="Pin"
      primaryPendingLabel="Pinning…"
      primaryDisabled={displayName.trim().length === 0}
      isPending={isMutating}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="pin-display-name">Name</Label>
        <Input
          id="pin-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={500}
          placeholder={defaultDisplayName}
        />
      </div>
    </FormDialog>
  );
};
